// CategoryList.jsx (updated part)
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { FiPlus, FiEdit2, FiTrash2, FiSearch } from 'react-icons/fi';
import { categoryService } from '../../services/categoryService';
import { productService } from '../../services/productService'; // Add this import
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import ErrorAlert from '../../components/Common/ErrorAlert';
import ConfirmDialog from '../../components/Common/ConfirmDialog';
import { formatDate } from '../../utils/formatters';
import { toast } from 'react-hot-toast';

const CategoryList = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const queryClient = useQueryClient();

  // Fetch categories
  const { data: categories, isLoading, error } = useQuery(
    'categories',
    categoryService.getAll
  );

  // Fetch products to get counts per category
  const { data: products } = useQuery(
    'products',
    () => productService.getAll({ limit: 1000 }),
    {
      enabled: true, // Always fetch products
    }
  );

  // Calculate product count per category
  const getProductCount = (categoryId) => {
    if (!products) return 0;
    return products.filter(product => product.category_id === categoryId).length;
  };

  const deleteMutation = useMutation(
    (id) => categoryService.delete(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('categories');
        toast.success('លុបប្រភេទដោយជោគជ័យ');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'មិនអាចលុបប្រភេទបានទេ');
      },
    }
  );

  const handleDelete = () => {
    if (deleteId) {
      deleteMutation.mutate(deleteId);
      setDeleteId(null);
    }
  };

  const filteredCategories = categories?.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    category.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorAlert message="មិនអាចផ្ទុកប្រភេទបានទេ" />;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">ប្រភេទ</h1>
        <Link
          to="/admin/categories/new"
          className="bg-red-600 text-white px-4 py-2 text-sm font-medium flex items-center hover:bg-red-700 transition-colors"
        >
          <FiPlus className="mr-2" /> បន្ថែមប្រភេទ
        </Link>
      </div>

      {/* Search */}
      <div className="relative">
        <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="ស្វែងរកប្រភេទ..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="w-full pl-10 pr-4 py-2 border border-gray-200 focus:outline-none focus:border-red-600"
        />
      </div>

      {/* Categories Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {filteredCategories?.map((category) => {
          const productCount = getProductCount(category.id);
          const hasProducts = productCount > 0;
          
          return (
            <div key={category.id} className="bg-white border border-gray-200 p-5 hover:border-red-200 transition-all">
              <div className="flex justify-between items-start">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">
                    {category.name}
                  </h3>
                  {category.description && (
                    <p className="text-gray-600 text-sm mb-4 line-clamp-2">
                      {category.description}
                    </p>
                  )}
                  <p className="text-xs text-gray-500">
                    បានបង្កើត: {formatDate(category.created_at)}
                  </p>
                </div>
                
                <div className="flex space-x-2">
                  <Link
                    to={`/admin/categories/edit/${category.id}`}
                    className="p-2 text-gray-600 hover:bg-red-50 hover:text-red-600 transition-colors"
                    title="កែប្រែ"
                  >
                    <FiEdit2 className="w-4 h-4" />
                  </Link>
                  <button
                    onClick={() => setDeleteId(category.id)}
                    className={`p-2 transition-colors ${
                      hasProducts 
                        ? 'text-gray-400 cursor-not-allowed' 
                        : 'text-gray-600 hover:bg-red-50 hover:text-red-600'
                    }`}
                    title={hasProducts ? 'មិនអាចលុបបានទេ ព្រោះមានផលិតផលក្នុងប្រភេទនេះ' : 'លុប'}
                    disabled={hasProducts}
                  >
                    <FiTrash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Product Count */}
              <div className="mt-4 pt-4 border-t border-gray-100">
                <span className="text-sm text-gray-600">
                  ផលិតផល: <span className="font-semibold text-gray-900">{productCount}</span>
                </span>
              </div>
            </div>
          );
        })}
      </div>

      {filteredCategories?.length === 0 && (
        <div className="text-center py-12">
          <p className="text-gray-500">រកមិនឃើញប្រភេទ</p>
        </div>
      )}

      {/* Delete Confirmation */}
      <ConfirmDialog
        isOpen={!!deleteId}
        onClose={() => setDeleteId(null)}
        onConfirm={handleDelete}
        title="លុបប្រភេទ"
        message="តើអ្នកប្រាកដថាចង់លុបប្រភេទនេះ? សកម្មភាពនេះមិនអាចត្រឡប់វិញបានទេ។"
        confirmText="លុប"
        cancelText="បោះបង់"
      />
    </div>
  );
};

export default CategoryList;